import { z } from 'zod';
/**
 * Per-platform overrides on a `post_destinations` row. Stored as JSON in the
 * `platform_options` column. Validated server-side at schedule time AND
 * re-validated by each adapter at publish time as a last-mile check.
 *
 * Add a new platform: append a member to this discriminated union AND to the
 * Platform type in ./platforms.ts. Keep both in lockstep.
 */
/** Per-platform body text override — when "Customize for each network" is on. */
const bodyOverrideField = { bodyOverride: z.string().optional() };
const mediaOverrideField = { mediaOverrideIds: z.array(z.string().min(1)).max(10).optional() };
const baseFields = {
    ...bodyOverrideField,
    ...mediaOverrideField,
    firstComment: z.string().max(2200).optional(),
    altText: z.string().max(1000).optional(),
};
export const facebookOptions = z.object({
    platform: z.literal('facebook'),
    ...baseFields,
    link: z.string().url().optional(),
    shareToStory: z.boolean().optional(),
    /**
     * Facebook Pages to @mention/tag in the post (requires the Page Mentions
     * feature). Each is a resolved {id,name}; the adapter injects `@[id]` tokens
     * into the message at publish time.
     */
    mentions: z
        .array(z.object({ id: z.string().min(1), name: z.string().min(1) }))
        .max(10)
        .optional(),
});
export const instagramOptions = z.object({
    platform: z.literal('instagram'),
    ...baseFields,
    postType: z.enum(['feed', 'reel', 'story', 'carousel']).default('feed'),
    shareToFeed: z.boolean().default(true),
    shareToStory: z.boolean().optional(),
    collaborators: z.array(z.string()).max(3).optional(),
});
export const linkedinOptions = z.object({
    platform: z.literal('linkedin'),
    ...baseFields,
    visibility: z.enum(['PUBLIC', 'CONNECTIONS']).default('PUBLIC'),
});
export const xOptions = z.object({
    platform: z.literal('x'),
    ...bodyOverrideField,
    ...mediaOverrideField,
    replySettings: z.enum(['everyone', 'mentioned_users', 'following']).default('everyone'),
    threadAfter: z.string().max(280).array().max(20).optional(),
});
export const youtubeOptions = z.object({
    platform: z.literal('youtube'),
    ...bodyOverrideField,
    ...mediaOverrideField,
    title: z.string().max(100).optional(),
    description: z.string().max(5000).optional(),
    tags: z.array(z.string()).max(50).optional(),
    categoryId: z.string().optional(),
    privacyStatus: z.enum(['public', 'unlisted', 'private']).default('public'),
    madeForKids: z.boolean().default(false),
    isShort: z.boolean().default(false),
});
export const tiktokOptions = z.object({
    platform: z.literal('tiktok'),
    ...bodyOverrideField,
    ...mediaOverrideField,
    flow: z.enum(['direct', 'draft']).default('draft'),
    privacyLevel: z.enum(['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY']).default('SELF_ONLY'),
    disableComment: z.boolean().default(false),
    disableDuet: z.boolean().default(false),
    disableStitch: z.boolean().default(false),
    brandedContent: z.boolean().default(false),
});
export const gbpOptions = z.object({
    platform: z.literal('gbp'),
    ...bodyOverrideField,
    ...mediaOverrideField,
    postType: z.enum(['standard', 'event', 'offer']).default('standard'),
    callToAction: z
        .object({
        type: z.enum(['BOOK', 'ORDER', 'SHOP', 'LEARN_MORE', 'SIGN_UP', 'CALL']),
        url: z.string().url().optional(),
    })
        .optional(),
});
export const whatsappOptions = z.object({
    platform: z.literal('whatsapp'),
    ...bodyOverrideField,
    ...mediaOverrideField,
    templateName: z.string().optional(),
    templateLanguage: z.string().optional(),
    templateVariables: z.record(z.string()).optional(),
    recipientPhone: z.string().regex(/^\+\d{8,15}$/),
});
export const threadsOptions = z.object({
    platform: z.literal('threads'),
    ...baseFields,
    /** Attach the first reply automatically after the root post publishes. */
    replyControl: z.enum(['everyone', 'accounts_you_follow', 'mentioned_only']).optional(),
});
export const platformOptionsSchema = z.discriminatedUnion('platform', [
    facebookOptions,
    instagramOptions,
    threadsOptions,
    linkedinOptions,
    xOptions,
    youtubeOptions,
    tiktokOptions,
    gbpOptions,
    whatsappOptions,
]);
//# sourceMappingURL=platform-options.schema.js.map